import React, { useCallback, useState } from 'react';
import { Download, Info } from 'lucide-react';

import { catalogApi } from 'api/catalog';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

import { ITEM_MASTER_COLUMN_DEFS } from './itemMasterBulkUploadColumns';
import { downloadItemMasterBulkUploadFieldReference } from './itemMasterBulkUploadFieldReference';
import { ItemMasterBulkUploadInstructionsDialog } from './ItemMasterBulkUploadInstructionsDialog';
import { downloadItemMasterBulkUploadTemplate } from './itemMasterBulkUploadTemplate';

export function BulkUploadItemMaster({ onPersistSuccess }) {
    const bulkUpload = useBulkUpload({
        api: {
            edit: (key, params, rowsToUpdate, rowsToDelete) =>
                catalogApi.bulkItemMasterEdit(key, params, rowsToUpdate, rowsToDelete),
            fetch: (key, params, signal) => catalogApi.bulkItemMasterFetch(key, params, signal),
            persist: (key, params) => catalogApi.bulkItemMasterPersist(key, params),
            upload: (file, params) => catalogApi.bulkItemMasterUpload(file, params),
        },
        params: {},
    });

    const [persistSuccess, setPersistSuccess] = useState(false);
    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const hasRowErrors = result?.persistResults?.errors?.length > 0;
            if (!hasRowErrors) {
                setPersistSuccess(true);
                bulkUpload.reset();
                onPersistSuccess?.();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload, onPersistSuccess]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
    }, []);

    return (
        <div className={cn('flex w-full flex-col gap-4', hasData && 'min-h-[calc(100dvh-14rem)]')}>
            <Card className={cn(hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                <CardContent className={cn('space-y-4', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                    <div className="flex items-start gap-2">
                        <CardDescription className="min-w-0 flex-1 leading-relaxed">
                            Upload a CSV to bulk create or update item masters. Rows are validated before you can save.
                        </CardDescription>
                        <ItemMasterBulkUploadInstructionsDialog />
                    </div>

                    {!hasData && !persistSuccess && (
                        <>
                            <div className="flex flex-wrap justify-end gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadItemMasterBulkUploadFieldReference}
                                >
                                    <Download className="mr-2 h-4 w-4" />
                                    Download Field Reference
                                </Button>
                                <Button variant="outline" size="sm" onClick={downloadItemMasterBulkUploadTemplate}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download Template
                                </Button>
                            </div>
                            <BulkUploadDropzone
                                onFileSelect={(file) => file && bulkUpload.uploadFile(file)}
                                disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                    bulkUpload.status,
                                )}
                                isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                            />
                        </>
                    )}

                    <BulkUploadStatus
                        entityName="Item Master"
                        fetchError={bulkUpload.fetchError}
                        handleStartNew={handleStartNew}
                        hasData={hasData}
                        persistError={bulkUpload.persistError}
                        persistErrors={bulkUpload.persistErrors}
                        persistSuccess={persistSuccess}
                        status={bulkUpload.status}
                        uploadError={bulkUpload.uploadError}
                    />

                    {hasData &&
                        !persistSuccess &&
                        !bulkUpload.hasPersistErrors &&
                        ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(bulkUpload.status) && (
                            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                                <div className="flex min-w-0 flex-col gap-3">
                                    {bulkUpload.showIssuesFilterHint && (
                                        <Alert
                                            className="w-full min-w-0 border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                            data-testid="bulk-item-master-filtered-view-hint"
                                            role="status"
                                        >
                                            <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                            <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                Showing only rows with validation issues, warnings, pending edits, or
                                                rows marked for deletion. Turn off the switch to see all rows.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {errorCount > 0 ? (
                                        <Alert variant="destructive" className="w-full min-w-0">
                                            <AlertDescription>
                                                {errorCount} validation error{errorCount !== 1 ? 's' : ''} found. Please
                                                fix the errors before saving. You can hover over the highlighted cells
                                                for more information.
                                            </AlertDescription>
                                        </Alert>
                                    ) : (
                                        <Alert className="w-full min-w-0 border-green-500 text-green-700">
                                            <AlertDescription className="text-green-700">
                                                No validation errors. Ready to save.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                        {bulkUpload.showIssuesRowFilter && (
                                            <BulkUploadRowFilterSwitch
                                                checked={bulkUpload.showIssuesOnly}
                                                disabled={bulkUpload.rows.length === 0}
                                                id="bulk-item-master-show-issues-only"
                                                onCheckedChange={bulkUpload.setShowIssuesOnly}
                                                className="shrink-0"
                                            />
                                        )}
                                        {bulkUpload.hasUnsavedChanges && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={bulkUpload.clearPendingChanges}
                                                >
                                                    Discard Changes
                                                </Button>
                                                <Button variant="default" size="sm" onClick={bulkUpload.submitChanges}>
                                                    Submit Changes
                                                </Button>
                                            </>
                                        )}
                                        <Button variant="outline" size="sm" onClick={bulkUpload.reset}>
                                            Start Over
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handlePersist}
                                            disabled={!bulkUpload.canPersist}
                                        >
                                            Save Item Masters
                                        </Button>
                                    </div>
                                </div>

                                <BulkUploadTable
                                    columnDefs={ITEM_MASTER_COLUMN_DEFS}
                                    fillAvailableHeight
                                    rows={bulkUpload.tableRows}
                                    errorsByCell={bulkUpload.errorsByCell}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    updateCell={bulkUpload.updateCell}
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    toggleRowDelete={bulkUpload.toggleRowDelete}
                                />

                                <BulkUploadPendingChanges
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    rowsLength={bulkUpload.rows.length}
                                />
                            </div>
                        )}
                </CardContent>
            </Card>
        </div>
    );
}
